<template>
    <base-dialog :loading="loading" :value="dialog" :width="560" :title="$t('chatMode.title')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="cm-body">
            <div class="cm-context">{{ $t('chatMode.subtitle', { name: studentName }) }}</div>
            <div class="cm-desc">{{ $t('chatMode.description') }}</div>

            <kimsa-toggle class="cm-enable" label-left :value="config.enabled" @change="config.enabled = $event">
                <div class="cm-enable-text">
                    <span class="cm-enable-label">{{ $t('chatMode.enabled') }}</span>
                    <span class="cm-enable-hint">{{ $t('chatMode.enabled_hint') }}</span>
                </div>
            </kimsa-toggle>

            <div v-if="nothingActive" class="cm-warning">
                <v-icon size="15" color="accent">mdi-alert-outline</v-icon>
                <span>{{ $t('chatMode.nothing_active') }}</span>
            </div>

            <skeleton-loader v-if="loading || !ready" type="rows" :count="4" />

            <v-expansion-panels v-else class="cm-panels" flat accordion>
                <v-expansion-panel v-for="category in categories" :key="category.key" class="cm-panel">
                    <v-expansion-panel-header class="cm-panel-header">
                        <div class="cm-panel-title">
                            <span class="cm-cat-name">{{ $t(`chatMode.${category.key}`) }}</span>
                            <span class="cm-cat-count">
                                {{ $t('chatMode.active_count', activeCount(category)) }}
                            </span>
                        </div>
                        <template v-slot:actions>
                            <v-icon size="18">mdi-chevron-down</v-icon>
                        </template>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="cm-panel-content">
                        <div class="cm-cat-hint">{{ $t(`chatMode.${category.key}_hint`) }}</div>

                        <div class="cm-phrases">
                            <div v-for="phrase in config[category.key].phrases" :key="phrase.id" class="cm-phrase">
                                <template v-if="editingId === phrase.id">
                                    <kimsa-text-field class="cm-phrase-input" dense no-elevation force-value
                                        :value="editingText" @change="editingText = $event"
                                        :placeholder="$t('chatMode.phrase_placeholder')" />
                                    <button-tooltip :tooltip="$t('common.save')">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-on="on" v-bind="attrs" :disabled="!editingText"
                                                @click="confirmEdit(category)">
                                                <v-icon size="18" color="primary">mdi-check</v-icon>
                                            </v-btn>
                                        </template>
                                    </button-tooltip>
                                    <button-tooltip :tooltip="$t('common.cancel')">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-on="on" v-bind="attrs" @click="cancelEdit">
                                                <v-icon size="18">mdi-close</v-icon>
                                            </v-btn>
                                        </template>
                                    </button-tooltip>
                                </template>

                                <template v-else>
                                    <v-simple-checkbox class="cm-phrase-check" color="primary" :ripple="false"
                                        :value="isActive(category, phrase)" @input="togglePhrase(category, phrase)" />
                                    <span class="cm-phrase-text" @click="togglePhrase(category, phrase)">
                                        {{ phrase.text }}
                                    </span>
                                    <button-tooltip :tooltip="$t('common.edit')">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-on="on" v-bind="attrs" @click="startEdit(phrase)">
                                                <v-icon size="16">mdi-pencil-outline</v-icon>
                                            </v-btn>
                                        </template>
                                    </button-tooltip>
                                    <button-tooltip :tooltip="$t('common.delete')">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-on="on" v-bind="attrs"
                                                @click="deletePhrase(category, phrase)">
                                                <v-icon size="16" color="error">$delete</v-icon>
                                            </v-btn>
                                        </template>
                                    </button-tooltip>
                                </template>
                            </div>

                            <div v-if="!config[category.key].phrases.length" class="cm-empty">
                                {{ $t('chatMode.no_phrases') }}
                            </div>
                        </div>

                        <div v-if="creatingKey === category.key" class="cm-phrase cm-phrase--new">
                            <kimsa-text-field class="cm-phrase-input" dense no-elevation force-value autofocus
                                :value="creatingText" @change="creatingText = $event"
                                :placeholder="$t('chatMode.phrase_placeholder')" />
                            <button-tooltip :tooltip="$t('common.add')">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-on="on" v-bind="attrs" :disabled="!creatingText"
                                        @click="confirmCreate(category)">
                                        <v-icon size="18" color="primary">mdi-check</v-icon>
                                    </v-btn>
                                </template>
                            </button-tooltip>
                            <button-tooltip :tooltip="$t('common.cancel')">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-on="on" v-bind="attrs" @click="cancelCreate">
                                        <v-icon size="18">mdi-close</v-icon>
                                    </v-btn>
                                </template>
                            </button-tooltip>
                        </div>

                        <v-btn v-else text small class="cm-add-btn" @click="startCreate(category)">
                            <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                            {{ $t('chatMode.new_phrase') }}
                        </v-btn>

                        <div class="cm-controls">
                            <div class="cm-control">
                                <span class="cm-control-label">{{ $t('chatMode.time') }}</span>
                                <kimsa-date-picker time dense solo :value="config[category.key].time"
                                    @change="setValue(category, 'time', $event)" />
                            </div>
                            <div class="cm-control">
                                <span class="cm-control-label">{{ controlLabel(category) }}</span>
                                <kimsa-combobox dense solo no-object :items="controlOptions(category)"
                                    :value="controlValue(category)"
                                    @change="setValue(category, controlField(category), $event)" />
                            </div>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>

        <template v-slot:footer>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text" :loading="loadingSave"
                :disabled="loading" @click="save">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { db } from "@/utils/Firebase";
import BaseDialog from "@/components/common/BaseDialog";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaDatePicker from "@/components/common/inputs/KimsaDatePicker";
import {
    CHAT_MODE_CATEGORIES,
    CHAT_MODE_FREQUENCIES,
    CHAT_MODE_PAYMENT_MOMENTS,
    chatModeConfigId,
    chatModePayload,
    newPhraseId,
    normalizeChatModeConfig,
} from "@/utils/ChatMode";

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default {
    name: "ChatModeDialog",
    components: {
        BaseDialog, ButtonTooltip, SkeletonLoader, KimsaToggle, KimsaCombobox, KimsaTextField, KimsaDatePicker,
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingSave: false,
            coachId: null,
            student: null,
            config: { enabled: false },
            editingId: null,
            editingText: '',
            creatingKey: null,
            creatingText: '',
        }
    },
    computed: {
        categories() {
            return CHAT_MODE_CATEGORIES
        },
        ready(vm = this) {
            return CHAT_MODE_CATEGORIES.every(category => !!vm.config[category.key])
        },
        nothingActive(vm = this) {
            if (!vm.ready || !vm.config.enabled) return false
            return CHAT_MODE_CATEGORIES.every(category => !vm.config[category.key].active.length)
        },
        studentName(vm = this) {
            return vm.student?.name || ''
        },
        studentId(vm = this) {
            return vm.student?.id || null
        },
        configId(vm = this) {
            return chatModeConfigId(vm.coachId, vm.studentId)
        },
        frequencyOptions(vm = this) {
            return CHAT_MODE_FREQUENCIES.map(value => ({ name: vm.$t('chatMode.trainings_percent', { n: value }), value }))
        },
        weeklyOptions(vm = this) {
            return WEEKDAYS.map((day, index) => ({ name: vm.$t(`days.${day}`), value: index + 1 }))
        },
        momentOptions(vm = this) {
            return CHAT_MODE_PAYMENT_MOMENTS.map(value => ({ name: vm.$t(`chatMode.${value}`), value }))
        },
    },
    methods: {
        async show(student, coachId) {
            const vm = this
            vm.reset()
            vm.student = student
            vm.coachId = coachId || vm.currentUser?.uid || null
            vm.config = normalizeChatModeConfig(null, key => vm.$t(key))
            vm.dialog = true
            await vm.loadConfig()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.student = null
            vm.coachId = null
            vm.config = { enabled: false }
            vm.editingId = null
            vm.editingText = ''
            vm.creatingKey = null
            vm.creatingText = ''
        },
        async loadConfig() {
            const vm = this
            if (!vm.configId) return
            await vm.withLoading(async () => {
                const doc = await db.collection('chatModeConfig').doc(vm.configId).get()
                vm.config = normalizeChatModeConfig(doc.exists ? doc.data() : null, key => vm.$t(key))
            })
        },
        activeCount(category) {
            const value = this.config[category.key]
            return { n: value?.active?.length || 0, total: value?.phrases?.length || 0 }
        },
        controlField(category) {
            if (category.control === 'weekday') return 'weekday'
            if (category.control === 'moment') return 'when'
            return 'frequency'
        },
        controlLabel(category) {
            const vm = this
            if (category.control === 'weekday') return vm.$t('chatMode.weekday')
            if (category.control === 'moment') return vm.$t('chatMode.moment')
            return vm.$t('chatMode.frequency')
        },
        controlOptions(category) {
            const vm = this
            if (category.control === 'weekday') return vm.weeklyOptions
            if (category.control === 'moment') return vm.momentOptions
            return vm.frequencyOptions
        },
        controlValue(category) {
            return this.config[category.key][this.controlField(category)]
        },
        setValue(category, field, value) {
            if (value === null || value === undefined) return
            this.$set(this.config[category.key], field, value)
        },
        isActive(category, phrase) {
            return this.config[category.key].active.includes(phrase.id)
        },
        togglePhrase(category, phrase) {
            const vm = this
            const value = vm.config[category.key]
            const index = value.active.indexOf(phrase.id)
            if (index >= 0) value.active.splice(index, 1)
            else value.active.push(phrase.id)
        },
        startEdit(phrase) {
            const vm = this
            vm.cancelCreate()
            vm.editingId = phrase.id
            vm.editingText = phrase.text
        },
        cancelEdit() {
            const vm = this
            vm.editingId = null
            vm.editingText = ''
        },
        confirmEdit(category) {
            const vm = this
            const text = (vm.editingText || '').trim()
            if (!text) return
            const phrase = vm.config[category.key].phrases.find(item => item.id === vm.editingId)
            if (phrase) vm.$set(phrase, 'text', text)
            vm.cancelEdit()
        },
        deletePhrase(category, phrase) {
            const vm = this
            const value = vm.config[category.key]
            value.phrases = value.phrases.filter(item => item.id !== phrase.id)
            value.active = value.active.filter(id => id !== phrase.id)
            if (vm.editingId === phrase.id) vm.cancelEdit()
        },
        startCreate(category) {
            const vm = this
            vm.cancelEdit()
            vm.creatingKey = category.key
            vm.creatingText = ''
        },
        cancelCreate() {
            const vm = this
            vm.creatingKey = null
            vm.creatingText = ''
        },
        confirmCreate(category) {
            const vm = this
            const text = (vm.creatingText || '').trim()
            if (!text) return
            const value = vm.config[category.key]
            const phrase = { id: newPhraseId(), text }
            value.phrases.push(phrase)
            value.active.push(phrase.id)
            vm.cancelCreate()
        },
        async save() {
            const vm = this
            if (!vm.configId) return
            await vm.withLoading(async () => {
                const payload = chatModePayload(vm.config, vm.coachId, vm.studentId)
                await db.collection('chatModeConfig').doc(vm.configId).set(payload, { merge: true })
                vm.toast(vm.$t('chatMode.saved'))
                vm.$emit('saved', payload)
                vm.hide()
            }, 'loadingSave')
        },
    },
}
</script>

<style scoped>
.cm-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cm-context {
    font-size: 12px;
    font-weight: 500;
    color: var(--v-accent-base);
    background: var(--v-surface2-base);
    border-radius: 8px;
    padding: 10px 12px;
}

.cm-desc {
    font-size: 12px;
    line-height: 1.5;
    color: var(--v-txtMuted-base);
}

.cm-enable {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.cm-enable-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cm-enable-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.cm-enable-hint {
    font-size: 12px;
    line-height: 1.4;
    color: var(--v-txtMuted-base);
}

.cm-warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--v-txtSecondary-base);
    background: var(--v-surface2-base);
    border-left: 3px solid var(--v-accent-base);
    border-radius: 8px;
    padding: 10px 12px;
}

.cm-panels {
    background: transparent;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.cm-panel {
    background: var(--v-surface-base) !important;
    border-bottom: 1px solid var(--v-outline-base);
}

.cm-panel:last-child {
    border-bottom: none;
}

.cm-panel::before {
    box-shadow: none !important;
}

.cm-panel-title {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cm-cat-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.cm-cat-count {
    font-size: 11px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
}

.cm-cat-hint {
    font-size: 12px;
    line-height: 1.5;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.cm-phrases {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.cm-phrase {
    display: flex;
    align-items: center;
    gap: 6px;
}

.cm-phrase-text {
    flex: 1;
    font-size: 12px;
    line-height: 1.45;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
}

.cm-phrase-input {
    flex: 1;
}

.cm-phrase-check {
    flex-shrink: 0;
}

.cm-empty {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 6px 0;
}

.cm-add-btn {
    align-self: flex-start;
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-accent-base);
}

.cm-controls {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 14px;
}

.cm-control-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>
